import { SlideHeader } from "../../../SlideHeader";

type StageAccent = "today" | "next" | "future" | "destination";

const STAGES: Array<{
  label: string;
  body: string;
  cadence: string;
  accent: StageAccent;
}> = [
  {
    label: "Today",
    body: "Insights run themselves. Daily audit, daily competitor, daily AI/web, daily store. Methodology updated weekly. Weekly Revision Plan publishes in one click.",
    cadence: "Expert ASO. Every app. Every day.",
    accent: "today",
  },
  {
    label: "In development",
    body: "Generate — drafts alternative metadata, icons, and screenshots in the customer's voice, reviewed before publish.",
    cadence: "Drafted for you. Reviewed by you.",
    accent: "next",
  },
  {
    label: "On the horizon",
    body: "Full Localize across every supported language. Anomaly alerting on KPI breaks. Closed-loop autonomy with human approval at publish.",
    cadence: "End-to-end, every market.",
    accent: "future",
  },
  {
    label: "The destination",
    body: "The closed loop. ASO runs itself — with human approval at every release gate.",
    cadence: "Autonomous ASO.",
    accent: "destination",
  },
];

export default function Roadmap() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8">
      <SlideHeader eyebrow="Roadmap">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-6xl md:leading-[1.1]">
          Closing the loop.
        </h1>
      </SlideHeader>

      {/* Product trajectory */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4 md:gap-4">
        {STAGES.map((s) => (
          <StageCard key={s.label} {...s} />
        ))}
      </div>

      {/* GTM trajectory */}
      <div className="mt-6 border-t border-slate-200 pt-5 md:mt-8 md:pt-6">
        <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 md:text-sm md:tracking-[0.25em]">
          GTM trajectory
        </p>
        <p className="mt-4 text-balance text-center text-sm leading-snug text-slate-700 md:mt-5 md:text-lg">
          Self-serve indies &amp; freelance consultants{" "}
          <span aria-hidden="true" className="mx-1 text-blue-500 md:mx-2">
            →
          </span>{" "}
          SMB growth-team SaaS{" "}
          <span aria-hidden="true" className="mx-1 text-blue-500 md:mx-2">
            →
          </span>{" "}
          Agency partnerships{" "}
          <span aria-hidden="true" className="mx-1 text-blue-500 md:mx-2">
            →
          </span>{" "}
          Enterprise tier
        </p>
      </div>

      {/* Closer */}
      <div className="mt-6 md:mt-auto md:pt-6">
        <div className="rounded-2xl bg-blue-50 px-5 py-5 ring-1 ring-blue-200 md:px-10 md:py-6">
          <p className="text-base italic leading-snug text-blue-900 md:text-2xl">
            The destination is{" "}
            <span className="font-semibold not-italic">Autonomous ASO</span>.
          </p>
          <p className="mt-2 text-sm leading-snug text-blue-800/80 md:text-base">
            — the first layer of a wider app-marketing operating system for
            app teams.
          </p>
        </div>
      </div>
    </div>
  );
}

function StageCard({
  label,
  body,
  cadence,
  accent,
}: {
  label: string;
  body: string;
  cadence: string;
  accent: StageAccent;
}) {
  const isToday = accent === "today";
  const isDestination = accent === "destination";
  return (
    <div
      className={`flex flex-col rounded-xl p-4 ring-1 md:p-5 ${
        isToday
          ? "bg-blue-50 ring-blue-300"
          : isDestination
            ? "bg-white ring-blue-300"
            : "bg-white ring-slate-200"
      }`}
    >
      <div className="flex items-center gap-2">
        {isToday ? (
          <span
            aria-hidden="true"
            className="inline-block h-2 w-2 rounded-full bg-blue-600"
          />
        ) : null}
        <p
          className={`text-xs font-bold uppercase tracking-[0.18em] md:text-sm md:tracking-[0.2em] ${
            isToday || isDestination ? "text-blue-700" : "text-slate-500"
          }`}
        >
          {label}
        </p>
      </div>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-700 md:mt-4 md:text-base md:leading-snug">
        {body}
      </p>
      <p
        className={`mt-3 border-t pt-3 text-xs italic leading-snug md:mt-4 md:text-sm ${
          isToday || isDestination
            ? "border-blue-200 text-blue-800"
            : "border-slate-200 text-slate-500"
        }`}
      >
        {cadence}
      </p>
    </div>
  );
}
